import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Card } from '@blueprintjs/core';
import useModalContext from 'components/context/modal';
import withAncestry from 'ecommerce/hoc/withAncestry';
import { TREE_TYPE, getPageOptionIds } from 'editor-utils/template';

import useEditorItemContext from '../../../context/editorItemContext';
import generateAncestryData from '../../../generator/generateAncestryData';
import useEditorContext from '../../../context/editorContext';
import PageOptionsSelect from '../../components/pageOptionsSelect';
import DialogPanelBody from '../../components/dialogPanelBody';
import AddPageButton from '../../components/addPageButton';
import useAncestryFamilyTreeContext from '../../../context/ancestryFamilyTreeContext';
import CoupleSelectTabs from '../../people/couples/coupleSelectTabs';
import AncestryWebSocketSynchronizer from '../../../new/components/ancestryWebSocketSynchronizer';

const PersonPanel = ({ ancestryAuth, onAddPages }) => {
  const { item, template } = useEditorItemContext();
  const modalContext = useModalContext();
  const editorContext = useEditorContext();
  const { projectPersons } = useAncestryFamilyTreeContext();

  const [couple, setCouple] = useState();
  const [selectedPageOptionIds, setSelectedPageOptionIds] = useState([]);

  const handleClick = async () => {
    const { treeId, treeName } = item.additionalInfo;
    const { person, partner } = couple;
    const persons = [person?.id, partner?.id].filter(id => !!id);
    const treeType = TREE_TYPE.SINGLE;
    const numberOfGenerations = 0;

    modalContext.show(
      <AncestryWebSocketSynchronizer
        ancestryAuth={ancestryAuth}
        accessToken={editorContext.accessToken}
        productId={template.id}
        persons={persons}
        numberOfGenerations={numberOfGenerations}
        treeName={treeName}
        treeType={treeType}
        checkSyncStatusFirst
        progressLabel="We’re turning your data from Ancestry.com into a project preview."
        onSuccess={async ({ forceUpdate, onProgress, close }) => {
          const { data, error } = await generateAncestryData(
            editorContext.accessToken,
            template,
            ancestryAuth,
            {
              type: "ancestry",
              arrayOfPersonIds: [persons],
              optionIds: selectedPageOptionIds,
              numberOfGenerations,
              withoutCover: true,
              treeId,
              treeName,
              treeType,
              forceUpdate,
            },
            item.photos,
            ({ pending, total }) => {
              onProgress(((total - pending) / total) * 100);
            },
          );

          if (error) {
            editorContext.trackEvent('ancestry_generator_error');
            close();
            modalContext.confirm(<>{error}</>, {
              title: 'Error creating your project',
              closeable: false,
              confirmLabel: 'OK',
            });
            return;
          }

          editorContext.setPhotos(item.id, data.photos);
          // TODO filter pages
          const pages = data.pages.filter(page => true);

          close();

          if (pages.length > 0) {
            onAddPages(pages);
          } else {
            modalContext.confirm(<>Sorry, we didn't find any data to selected person(s).</>, {
              title: 'No pages added',
              closeable: false,
              confirmLabel: 'OK',
            });
          }
        }}
      />,
      {
        title: 'Building your project',
        closeable: false,
      },
    );
  };

  const enabled = couple?.person && selectedPageOptionIds.length > 0;

  return (
    <DialogPanelBody actionButton={<AddPageButton disabled={!enabled} onClick={handleClick} />}>
      <CoupleSelectTabs
        isLoading={projectPersons == null}
        onSelect={setCouple}
        people={projectPersons}
        treeId={item.additionalInfo.treeId}
      />
      <Card>
        <PageOptionsSelect
          pageOptionIds={getPageOptionIds(template, {
            withoutTOC: true,
            withoutFamilyTree: true,
            withoutRecords: true,
            withoutPhotos: true,
            withoutTitle: true,
            withoutDedication: true,
            withoutDivider: true,
          })}
          onChange={setSelectedPageOptionIds}
        />
      </Card>
    </DialogPanelBody>
  );
};

PersonPanel.propTypes = {
  onAddPages: PropTypes.func.isRequired,
};

export default withAncestry(PersonPanel);
